import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/base/buttons'
import { Select, type SelectOption } from '@/components/ui/Select'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/Modal'

interface WidgetTemplateNameModalProps {
  isOpen: boolean
  /** Pre-filled when renaming; empty when naming a new template. */
  initialName: string
  customerOptions?: SelectOption[]
  initialCustomerId?: number
  isCustomerRequired?: boolean
  isSaving?: boolean
  onClose: () => void
  onConfirm: (name: string, customerId?: number) => void
}

/** Naming a template, used both when creating one and when renaming it. */
export function WidgetTemplateNameModal({
  isOpen,
  initialName,
  customerOptions,
  initialCustomerId,
  isCustomerRequired,
  isSaving,
  onClose,
  onConfirm,
}: WidgetTemplateNameModalProps) {
  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <WidgetTemplateNameForm
          initialName={initialName}
          customerOptions={customerOptions}
          initialCustomerId={initialCustomerId}
          isCustomerRequired={isCustomerRequired}
          isSaving={isSaving}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      </ModalContent>
    </Modal>
  )
}

type WidgetTemplateNameFormProps = Omit<WidgetTemplateNameModalProps, 'isOpen'>

/**
 * Split out from the modal so the field mounts with the dialog: every time it
 * opens the draft starts from the name as it stands, without syncing state.
 */
function WidgetTemplateNameForm({
  initialName,
  customerOptions,
  initialCustomerId,
  isCustomerRequired,
  isSaving,
  onClose,
  onConfirm,
}: WidgetTemplateNameFormProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.name_modal'

  const [name, setName] = useState(initialName)
  const [customerId, setCustomerId] = useState<number | undefined>(initialCustomerId)
  const trimmed = name.trim()

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (trimmed && (!isCustomerRequired || customerId !== undefined)) {
      onConfirm(trimmed, customerId)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <ModalHeader onClose={onClose} bordered={false}>
        <ModalTitle>{t(`${base}.title`)}</ModalTitle>
      </ModalHeader>

      <ModalBody>
        <div className="space-y-4">
          <input
            autoFocus
            type="text"
            autoComplete="off"
            value={name}
            aria-label={t(`${base}.title`)}
            placeholder={t(`${base}.placeholder`)}
            onChange={(event) => setName(event.target.value)}
            className="h-11 w-full rounded-md border border-gray-200 bg-white px-3.5 text-base text-gray-900 shadow-xs placeholder:text-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-gray-500"
          />
          {customerOptions && (
            <Select
              label={t(`${base}.customer_label`)}
              placeholder={t(`${base}.customer_placeholder`)}
              options={customerOptions}
              selectedKey={customerId?.toString() ?? null}
              onSelectionChange={(key) => setCustomerId(key ? Number(key) : undefined)}
              isRequired={isCustomerRequired}
            />
          )}
        </div>
      </ModalBody>

      <ModalFooter bordered={false} align="stretch">
        <Button color="secondary" size="lg" onPress={onClose}>
          {t('resources.generic.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          size="lg"
          isDisabled={!trimmed || (isCustomerRequired && customerId === undefined)}
          isLoading={isSaving}
        >
          {t(`${base}.confirm`)}
        </Button>
      </ModalFooter>
    </form>
  )
}
